// Shared date navigation: day stepper, "Idag" button, date picker, Dag/Vecka/Alla toggle.

// Returns "YYYY-MM-DD" in local time
export const toLocalDate = (date) => date.toLocaleDateString("sv-SE");

// Parse a "YYYY-MM-DD" string at local noon to avoid timezone edge cases
export const atNoon = (dateStr) => new Date(dateStr + "T12:00:00");

export const addDays = (dateStr, n) => {
    const d = atNoon(dateStr);
    d.setDate(d.getDate() + n);
    return toLocalDate(d);
};

export const getWeekBounds = (date) => {
    const d = new Date(date);
    const day = d.getDay(); // 0=Sun
    const diffToMon = (day === 0 ? -6 : 1 - day);
    const mon = new Date(d); mon.setDate(d.getDate() + diffToMon); mon.setHours(0, 0, 0, 0);
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6); sun.setHours(23, 59, 59, 999);
    return { mon, sun };
};

const isoWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - day);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

const dayLabel = (dateStr) => {
    const today = toLocalDate(new Date());
    const formatted = atNoon(dateStr).toLocaleDateString("sv-SE", { weekday: "short", day: "numeric", month: "short" });
    if (dateStr === today) return "Idag · " + formatted;
    if (dateStr === addDays(today, 1)) return "Imorgon · " + formatted;
    if (dateStr === addDays(today, -1)) return "Igår · " + formatted;
    return formatted;
};

const weekLabel = (dateStr) => {
    const { mon, sun } = getWeekBounds(atNoon(dateStr));
    const fmt = d => d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
    return `v.${isoWeekNumber(mon)} · ${fmt(mon)}–${fmt(sun)}`;
};

// Capitalized full-day heading for week-view group rows, e.g. "Måndag 21 juli"
export const groupHeading = (dateStr) => {
    const label = atNoon(dateStr).toLocaleDateString("sv-SE", { weekday: "long", day: "numeric", month: "short" });
    return label.charAt(0).toUpperCase() + label.slice(1);
};

const VIEW_MODES = [
    { key: "day",  label: "Dag" },
    { key: "week", label: "Vecka" },
    { key: "all",  label: "Alla" },
];

const navBtn = "px-2.5 py-1.5 rounded-lg text-sm border transition bg-white text-gray-600 border-gray-300 hover:bg-gray-50";

const DateNavBar = ({ viewMode, anchorDate, onViewModeChange, onAnchorDateChange }) => {
    const today = toLocalDate(new Date());

    const step = (dir) => onAnchorDateChange(addDays(anchorDate, dir * (viewMode === "week" ? 7 : 1)));

    const isViewingToday = viewMode === "day" ? anchorDate === today
        : viewMode === "week" ? weekLabel(anchorDate) === weekLabel(today)
        : false;

    const goToToday = () => {
        onAnchorDateChange(today);
        if (viewMode === "all") onViewModeChange("day");
    };

    const handleDateChange = (e) => {
        if (!e.target.value) return;
        onAnchorDateChange(e.target.value);
        if (viewMode === "all") onViewModeChange("day");
    };

    return (
        <div className="flex flex-wrap items-center gap-2 mb-4">
            {viewMode !== "all" && (
                <div className="flex items-center gap-1">
                    <button onClick={() => step(-1)} className={navBtn} title={viewMode === "week" ? "Föregående vecka" : "Föregående dag"}>
                        ◀
                    </button>
                    <span className="min-w-[10rem] text-center text-sm font-medium text-gray-800 select-none">
                        {viewMode === "week" ? weekLabel(anchorDate) : dayLabel(anchorDate)}
                    </span>
                    <button onClick={() => step(1)} className={navBtn} title={viewMode === "week" ? "Nästa vecka" : "Nästa dag"}>
                        ▶
                    </button>
                </div>
            )}
            <button
                onClick={goToToday}
                disabled={isViewingToday}
                className={navBtn + (isViewingToday ? " opacity-40 cursor-default" : "")}
            >
                Idag
            </button>
            <input
                type="date"
                value={viewMode === "all" ? "" : anchorDate}
                onChange={handleDateChange}
                className="px-2.5 py-1.5 rounded-lg text-sm border border-gray-300 bg-white hover:bg-gray-50 transition cursor-pointer"
                title="Hoppa till datum"
            />
            <div className="flex ml-auto">
                {VIEW_MODES.map((mode, i) => (
                    <button
                        key={mode.key}
                        onClick={() => onViewModeChange(mode.key)}
                        className={
                            "px-3 py-1.5 text-sm border transition " +
                            (i === 0 ? "rounded-l-lg " : "-ml-px ") +
                            (i === VIEW_MODES.length - 1 ? "rounded-r-lg " : "") +
                            (viewMode === mode.key
                                ? "bg-blue-600 text-white border-blue-600 font-medium relative z-10"
                                : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50")
                        }
                    >
                        {mode.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

export default DateNavBar;
